import { useEffect, useState } from "react";
import { X, Loader2, Save, Trash2, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * iter160 — Create / Edit modal for Deliverables Pipeline tasks.
 * When `task` is null → create mode (POST). When set → edit mode (PATCH).
 * Kept intentionally small — the pipeline itself owns re-fetch after save.
 */
export default function DeliverableEditModal({
  task = null,
  defaultCouple = "",
  bookings = [],
  staff = [],
  onClose,
  onSaved,
  onDeleted,
  onDuplicated,
}) {
  const isEdit = !!task;
  const [form, setForm] = useState({
    title: task?.title || "",
    booking_id: task?.booking_id || "",
    couple_name: task?.couple_name || defaultCouple || "",
    category: task?.category || "",
    due_date: task?.due_date || "",
    priority: task?.priority || "medium",
    assigned_staff_id: task?.assigned_staff_id || "",
    notes: task?.notes || "",
    status: task?.status || "pending",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/post-production/tasks/${task.id}`, {
          title: form.title,
          category: form.category,
          due_date: form.due_date,
          priority: form.priority,
          assigned_staff_id: form.assigned_staff_id || null,
          notes: form.notes,
          status: form.status,
        });
        toast.success("Deliverable updated");
      } else {
        await api.post("/post-production/tasks", {
          title: form.title,
          booking_id: form.booking_id,
          couple_name: form.couple_name,
          category: form.category,
          due_date: form.due_date,
          priority: form.priority,
          assigned_staff_id: form.assigned_staff_id,
          notes: form.notes,
          status: form.status,
        });
        toast.success("Deliverable created");
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  const duplicate = async () => {
    if (!isEdit) return;
    setBusy(true);
    try {
      await api.post(`/post-production/tasks/${task.id}/duplicate`);
      toast.success("Deliverable duplicated");
      onDuplicated?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not duplicate");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!isEdit) return;
    if (!window.confirm(`Delete "${task.title || 'this deliverable'}"?\n\nIt will be moved to Trash and can be restored.`)) return;
    setBusy(true);
    try {
      await api.delete(`/post-production/tasks/${task.id}`);
      toast.success("Moved to Trash");
      onDeleted?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="deliverable-edit-modal"
    >
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200 sticky top-0 bg-white z-10">
          <h2 className="font-heading text-lg font-semibold text-stone-900">
            {isEdit ? "Edit Deliverable" : "Add Deliverable"}
          </h2>
          <button type="button" onClick={onClose} className="p-1.5 rounded hover:bg-stone-100" aria-label="Close" data-testid="deliverable-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Title *" testid="deliverable-title">
            <input required maxLength={200} value={form.title} onChange={change("title")} className="input w-full" placeholder="e.g. Wedding Highlight Reel" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Booking" testid="deliverable-booking">
              <select value={form.booking_id} onChange={change("booking_id")} className="input w-full text-sm">
                <option value="">— None —</option>
                {bookings.map((b) => (
                  <option key={b.id} value={b.id}>{b.client_name}{b.day1 ? ` · ${b.day1}` : ""}</option>
                ))}
              </select>
            </Field>
            <Field label="Category" testid="deliverable-category">
              <input maxLength={80} value={form.category} onChange={change("category")} className="input w-full" placeholder="e.g. Video, Photo, Album" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Due Date" testid="deliverable-due">
              <input type="date" value={form.due_date} onChange={change("due_date")} className="input w-full" />
            </Field>
            <Field label="Priority" testid="deliverable-priority">
              <select value={form.priority} onChange={change("priority")} className="input w-full">
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </Field>
          </div>
          <Field label="Assigned Editor" testid="deliverable-editor">
            <select value={form.assigned_staff_id} onChange={change("assigned_staff_id")} className="input w-full">
              <option value="">Unassigned</option>
              {staff.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Status" testid="deliverable-status">
            <select value={form.status} onChange={change("status")} className="input w-full">
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="done">Done</option>
            </select>
          </Field>
          <Field label="Notes" testid="deliverable-notes">
            <textarea rows={3} maxLength={2000} value={form.notes} onChange={change("notes")} className="input w-full text-sm" placeholder="Optional notes for the editor…" />
          </Field>
        </div>
        <div className="px-5 py-3 border-t border-stone-200 flex items-center justify-between gap-2 bg-white sticky bottom-0">
          <div className="flex items-center gap-1">
            {isEdit && (
              <>
                <button type="button" onClick={duplicate} disabled={busy} className="btn btn-outline btn-sm inline-flex items-center gap-1" data-testid="deliverable-duplicate-btn">
                  <Copy className="w-3.5 h-3.5" /> Duplicate
                </button>
                <button type="button" onClick={remove} disabled={busy} className="btn btn-sm inline-flex items-center gap-1 text-red-700 hover:bg-red-50 border border-red-200" data-testid="deliverable-delete-btn">
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} disabled={busy} className="btn btn-outline btn-sm" data-testid="deliverable-cancel-btn">Cancel</button>
            <button type="submit" disabled={busy} className="btn btn-primary btn-sm inline-flex items-center gap-1" data-testid="deliverable-save-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {isEdit ? "Save" : "Create"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid ? `${testid}-field` : undefined}>
      <span className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold block mb-1">{label}</span>
      {children}
    </label>
  );
}
